import { createChunks, uniqueValues } from '@shared/utils/array';
import { toQueryParams } from '@shared/utils/url';
import { subWeeks } from 'date-fns';
import { flatten } from 'lodash-es';
import { v1 } from '@/api/client';
import type {
  BucketOperation,
  DerivedSignalName,
  Iso8601Duration,
  SampleDto,
  SampleFilter,
  SignalHistogramData,
  SignalName,
} from './api.types';
import { sampleDataSchema, signalHistogramDataSchema } from './api-schema';
import { getBucketOperation } from './bucket';
import { BUCKET_WIDTH_DEFAULT } from './config';

type Interpolation = 'enabled' | 'disabled';
type Extrapolation = 'start' | 'end' | 'both' | 'disabled';

interface BaseSampleParams {
  signal_names: SignalName[];
  ignore_no_permit?: boolean;
}

export type SampleParams = SampleParamsWithBucket | SampleParamsWithFilter;

export interface SampleParamsWithBucket extends BaseSampleParams {
  start_time?: Date;
  end_time?: Date;
  bucket_width?: Iso8601Duration | null;
  bucket_operation?: BucketOperation[];
  interpolation?: Interpolation;
  extrapolation?: Extrapolation;
  filter?: never;
}

interface SampleParamsWithFilter extends BaseSampleParams {
  start_time?: Date;
  end_time?: Date;
  bucket_width?: never;
  bucket_operation?: never;
  interpolation?: never;
  extrapolation?: never;
  filter?: SampleFilter;
}

export interface SampleParamsStrict {
  signal_names: SignalName[];
  start_time: string;
  end_time: string;
  page_size: number;
  bucket_width?: Iso8601Duration;
  bucket_operation?: BucketOperation;
  filter?: SampleFilter;
  interpolation?: Interpolation;
  extrapolation?: Extrapolation;
  include_null?: boolean;
}

export async function fetchSamples(
  equipmentIds: string[],
  params: SampleParams
) {
  const query: SampleParamsStrict = {
    signal_names: uniqueValues(params.signal_names),
    start_time: (params.start_time || subWeeks(new Date(), 1)).toISOString(),
    end_time: (params.end_time || new Date()).toISOString(),
    page_size: 1000,
    include_null: true,
  };
  if (params.filter) {
    query.filter = params.filter;
  } else {
    const bucketOperation = getBucketOperation(params.bucket_operation);
    if (bucketOperation !== undefined) {
      query.bucket_operation = bucketOperation;
      query.bucket_width = params.bucket_width || BUCKET_WIDTH_DEFAULT;
      query.interpolation = params.interpolation;
      query.extrapolation = params.extrapolation;
    }
  }

  const chunks = createChunks(equipmentIds, 100);

  const samples = await Promise.all(
    chunks.map((chunk) => {
      return v1.getPaginated<SampleDto>(
        'equipment/sample',
        {
          ...query,
          equipment_ids: chunk,
        },
        {
          schema: sampleDataSchema,
        }
      );
    })
  );
  return flatten(samples);
}

export interface LatestSampleParams extends BaseSampleParams {
  max_age?: Iso8601Duration;
  bucket_width?: never;
  bucket_operation?: never;
  filter?: never;
}

export const fetchLatestSamples = (
  equipmentIds: string[],
  params: LatestSampleParams
) => {
  const chunks = createChunks(equipmentIds, 100);

  return Promise.all(
    chunks.map((chunk) => {
      const query = toQueryParams({ ...params, equipment_ids: chunk });
      return v1.get<SampleDto[]>(`equipment/sample/latest${query}`, {
        defaultValue: [],
        schema: sampleDataSchema,
      });
    })
  ).then((result) => flatten(result));
};

export interface DerivedSamplesParams {
  signal_names: DerivedSignalName[];
  start_time?: Date;
  end_time?: Date;
  ignore_no_permit?: boolean;
}

interface DerivedSamplesParamsStrict {
  signal_names: DerivedSignalName[];
  start_time: string;
  end_time: string;
  ignore_no_permit?: boolean;
}

export const fetchDerivedSamples = (
  equipmentIds: string[],
  params: DerivedSamplesParams
) => {
  const query: DerivedSamplesParamsStrict = {
    signal_names: params.signal_names,
    start_time: (params.start_time || subWeeks(new Date(), 1)).toISOString(),
    end_time: (params.end_time || new Date()).toISOString(),
  };

  const chunks = createChunks(equipmentIds, 100);

  return Promise.all(
    chunks.map((chunk) => {
      return v1.getPaginated<SampleDto>(
        'equipment/sample/derived',
        {
          ...query,
          equipment_ids: chunk,
        },
        {
          schema: sampleDataSchema,
        }
      );
    })
  ).then(flatten);
};

export interface SignalHistogramParams {
  signalNames: string[];
  startTime: Date;
  endTime: Date;
  buckets: number[];
}

export const fetchSignalHistogram = (
  equipmentId: string,
  { signalNames, startTime, endTime, buckets }: SignalHistogramParams
) => {
  if (!equipmentId || signalNames.length === 0) {
    return [];
  }
  const queryParams = toQueryParams({
    start_time: startTime.toISOString(),
    end_time: endTime.toISOString(),
    buckets,
  });

  return Promise.all(
    signalNames.map((signalName) =>
      v1.get<SignalHistogramData>(
        `equipment/${equipmentId}/signalname/${signalName}/histogram${queryParams}`,
        { defaultValue: [], schema: signalHistogramDataSchema }
      )
    )
  );
};
